"use client";

import { useState, useEffect, useRef, FormEvent, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Loader2,
  Check,
  LayoutTemplate,
  SlidersHorizontal,
  UserRound,
  Code2,
  ShoppingBag,
  AppWindow,
  MousePointerClick,
  RefreshCw,
  HelpCircle,
  ShoppingCart,
  CalendarCheck,
  Languages,
  FileText,
  Newspaper,
  Search,
  BarChart3,
  Plug,
  Sparkles,
  Bot,
  PenLine,
  Palette,
  Wrench,
  Globe,
  type LucideIcon,
} from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { sendForm } from "@/lib/sendForm";
import { FormGuard, type FormGuardHandle } from "@/components/ui/FormGuard";

type FormStatus = "idle" | "loading" | "success" | "error";

const STORAGE_KEY = "website_inquiry_draft";
const EASE = [0.22, 1, 0.36, 1] as const;

interface FieldState {
  websiteType: string;
  features: string[];
  timeline: string;
  pages: string;
  hasContent: string;
  industry: string;
  reference: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  details: string;
}

const EMPTY_FIELDS: FieldState = {
  websiteType: "",
  features: [],
  timeline: "",
  pages: "",
  hasContent: "",
  industry: "",
  reference: "",
  name: "",
  email: "",
  phone: "",
  company: "",
  details: "",
};

function loadDraft(): FieldState {
  if (typeof window === "undefined") return EMPTY_FIELDS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...EMPTY_FIELDS, ...JSON.parse(raw) } : EMPTY_FIELDS;
  } catch {
    return EMPTY_FIELDS;
  }
}

const STEP_ICONS: LucideIcon[] = [LayoutTemplate, SlidersHorizontal, UserRound];

const TYPE_ICONS: Record<string, LucideIcon> = {
  custom: Code2,
  ecommerce: ShoppingBag,
  webapp: AppWindow,
  landing: MousePointerClick,
  redesign: RefreshCw,
  notsure: HelpCircle,
};

const FEATURE_ICONS: Record<string, LucideIcon> = {
  ecommerce: ShoppingCart,
  booking: CalendarCheck,
  multilang: Languages,
  cms: FileText,
  blog: Newspaper,
  accounts: UserRound,
  seo: Search,
  analytics: BarChart3,
  integrations: Plug,
  animations: Sparkles,
  ai: Bot,
  copywriting: PenLine,
  branding: Palette,
  maintenance: Wrench,
};

/** Blueprint preview shows at most this many tiles; extras collapse into a "+N" tile. */
const MAX_PREVIEW_TILES = 4;

const INPUT =
  "w-full h-12 rounded-xl border border-white/[0.08] bg-white/[0.03] px-4 text-[16px] text-white placeholder:text-white/25 outline-none transition-[border-color,background-color,box-shadow] duration-200 hover:border-white/15 focus:border-[#FFB76C]/50 focus:bg-white/[0.05] focus:ring-4 focus:ring-[#FF419D]/10";

export function WebsiteInquiryForm() {
  const { t } = useLang();
  const wi = t.websiteInquiry;

  const [formStatus, setFormStatus] = useState<FormStatus>("idle");
  const guard = useRef<FormGuardHandle>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [fields, setFields] = useState<FieldState>(EMPTY_FIELDS);
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const cardRef = useRef<HTMLDivElement>(null);

  // Hydrate from localStorage after mount (avoids SSR mismatch)
  useEffect(() => {
    setFields(loadDraft());
  }, []);

  // Persist on every change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fields));
    } catch {}
  }, [fields]);

  const { websiteType, features, timeline, pages, hasContent, industry, reference, name, email, phone, company, details } =
    fields;

  const setField =
    <K extends keyof FieldState>(key: K) =>
    (value: FieldState[K]) =>
      setFields((f) => ({ ...f, [key]: value }));

  const setText =
    (key: keyof FieldState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setFields((f) => ({ ...f, [key]: e.target.value }));

  const toggleFeature = (id: string) =>
    setFields((f) => ({
      ...f,
      features: f.features.includes(id) ? f.features.filter((x) => x !== id) : [...f.features, id],
    }));

  // Resolve an option id to its localized title for a readable submission
  const labelFor = (list: ReadonlyArray<{ id: string; title: string }>, id: string) =>
    list.find((o) => o.id === id)?.title ?? id;

  const lastStep = STEP_ICONS.length - 1;
  const canAdvance = step !== 0 || Boolean(websiteType);

  const goTo = (next: number) => {
    setDirection(next > step ? 1 : -1);
    setStep(next);
    // Keep the top of the form in view when a step changes (mostly for mobile)
    const top = cardRef.current?.getBoundingClientRect().top ?? 0;
    if (top < 0) cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Enter on an earlier step moves forward instead of submitting
    if (step < lastStep) {
      if (canAdvance) goTo(step + 1);
      return;
    }
    setFormStatus("loading");
    setErrorMessage("");

    try {
      await sendForm("website", {
        name,
        email,
        phone,
        website_type: labelFor(wi.types, websiteType),
        industry,
        features: features.map((id) => labelFor(wi.features, id)).join(", "),
        timeline: timeline && labelFor(wi.timelines, timeline),
        pages: pages && labelFor(wi.pages, pages),
        has_content: hasContent && labelFor(wi.hasContent, hasContent),
        reference,
        company,
        message: details,
      }, guard.current?.get() ?? { token: "", trap: "" });
      setFormStatus("success");
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
    } catch {
      setFormStatus("error");
      setErrorMessage(wi.errorFull);
    }
    guard.current?.reset();
  };

  const typeTitle = websiteType ? labelFor(wi.types, websiteType) : "";
  const overflowing = features.length > MAX_PREVIEW_TILES;
  const visibleFeatures = overflowing ? features.slice(0, MAX_PREVIEW_TILES - 1) : features;
  const hiddenFeatures = overflowing ? features.slice(MAX_PREVIEW_TILES - 1) : [];
  // Each answer carries a short name, so the preview shows which question it answers
  const picked = [
    pages && { label: wi.previewPagesLabel, value: labelFor(wi.pages, pages) },
    hasContent && { label: wi.previewContentLabel, value: labelFor(wi.hasContent, hasContent) },
    timeline && { label: wi.previewTimelineLabel, value: labelFor(wi.timelines, timeline) },
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    // id: the chat assistant scrolls here (lib/chat/navigation.ts)
    <section id="website-inquiry" className="relative section-padding overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/3 h-[700px] w-[700px] -translate-x-1/2 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(224,64,160,0.12), transparent 65%)", filter: "blur(80px)" }}
      />

      <div className="container relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: EASE }}
          className="max-w-4xl mx-auto text-center mb-10 sm:mb-14"
        >
          <span className="inline-block text-[11px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-5 text-[#f472b6]">
            {wi.label}
          </span>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.04] tracking-tight mb-5">
            {wi.title} <span className="text-gradient-warm">{wi.titleAccent}</span>
          </h2>
          <p className="text-white/50 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">{wi.sub}</p>
        </motion.div>

        <div className="mx-auto grid max-w-6xl grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* ── Wizard ── */}
          <motion.div
            ref={cardRef}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: EASE }}
            className="lg:col-span-7 relative scroll-mt-28 rounded-[22px] sm:rounded-[28px] border border-white/[0.09] p-5 sm:p-8 lg:p-10"
            style={{ background: "linear-gradient(160deg, rgba(255,255,255,0.05), rgba(255,255,255,0.015))" }}
          >
            <span
              aria-hidden
              className="absolute top-0 left-1/4 right-1/4 h-px"
              style={{ background: "linear-gradient(90deg, transparent, rgba(224,64,160,0.6), transparent)" }}
            />

            {formStatus === "success" ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="flex flex-col items-center justify-center gap-4 py-14 sm:py-20 text-center"
              >
                <motion.span
                  className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#FFB76C] to-[#E040A0] shadow-[0_0_60px_-10px_rgba(224,64,160,0.7)]"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.1 }}
                >
                  <CheckCircle size={38} className="text-white" />
                </motion.span>
                <h3 className="font-display text-2xl sm:text-3xl text-white">{wi.successTitle}</h3>
                <p className="max-w-sm text-base sm:text-lg text-white/60">{wi.successSub}</p>
                <button
                  type="button"
                  onClick={() => {
                    setFormStatus("idle");
                    setFields(EMPTY_FIELDS);
                    setStep(0);
                  }}
                  className="mt-2 inline-flex min-h-[44px] items-center rounded-full border border-white/10 px-5 text-sm text-white/80 transition-colors hover:border-white/25 hover:text-white cursor-pointer"
                >
                  {wi.sendAnother}
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-7">
                {/* Progress */}
                <div className="flex items-center gap-2 sm:gap-3">
                  {STEP_ICONS.map((Icon, i) => {
                    const done = i < step;
                    const current = i === step;
                    return (
                      <div key={i} className="flex flex-1 items-center gap-2 sm:gap-3 last:flex-none">
                        <button
                          type="button"
                          onClick={() => (i < step || (i === step + 1 && canAdvance) ? goTo(i) : undefined)}
                          aria-current={current ? "step" : undefined}
                          aria-label={[wi.typeLabel, wi.featuresLabel, wi.nameLabel][i]}
                          className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-all duration-300 cursor-pointer"
                          style={{
                            borderColor: current || done ? "rgba(255,65,157,0.6)" : "rgba(255,255,255,0.1)",
                            background: done
                              ? "linear-gradient(135deg, #FFB76C, #FF419D)"
                              : current
                                ? "rgba(255,65,157,0.15)"
                                : "rgba(255,255,255,0.03)",
                            color: done ? "#fff" : current ? "#FF6FB5" : "rgba(255,255,255,0.35)",
                          }}
                        >
                          {done ? <Check size={16} strokeWidth={3} /> : <Icon size={16} />}
                        </button>
                        {i < lastStep && (
                          <span className="relative h-px flex-1 overflow-hidden bg-white/10">
                            <motion.span
                              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#FFB76C] to-[#FF419D]"
                              animate={{ width: i < step ? "100%" : "0%" }}
                              transition={{ duration: 0.5, ease: EASE }}
                            />
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <AnimatePresence mode="wait" initial={false} custom={direction}>
                  <motion.div
                    key={step}
                    className="flex flex-col gap-7"
                    initial={{ opacity: 0, x: direction * 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: direction * -30 }}
                    transition={{ duration: 0.3, ease: EASE }}
                  >
                    {step === 0 && (
                      <>
                        <Group label={wi.typeLabel} required>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3">
                            {wi.types.map((opt) => {
                              const on = websiteType === opt.id;
                              const Icon = TYPE_ICONS[opt.id] ?? Code2;
                              return (
                                <button
                                  key={opt.id}
                                  type="button"
                                  aria-pressed={on}
                                  onClick={() => setField("websiteType")(opt.id)}
                                  className={`group relative flex min-h-[112px] flex-col gap-2 rounded-2xl border p-3.5 sm:p-4 text-left transition-all duration-200 cursor-pointer ${
                                    on
                                      ? "border-[#FF419D]/55 bg-[#FF419D]/[0.1] shadow-[0_12px_40px_-16px_rgba(255,65,157,0.6)]"
                                      : "border-white/[0.08] bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]"
                                  }`}
                                >
                                  <span
                                    className={`flex h-9 w-9 items-center justify-center rounded-xl transition-colors ${
                                      on ? "bg-gradient-to-br from-[#FFB76C] to-[#FF419D] text-white" : "bg-white/[0.05] text-white/55"
                                    }`}
                                  >
                                    <Icon size={17} />
                                  </span>
                                  <span className="text-[15px] font-semibold leading-tight text-white">{opt.title}</span>
                                  <span className="text-xs sm:text-[13px] leading-snug text-white/45">{opt.desc}</span>
                                  {on && (
                                    <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[#FF419D]">
                                      <Check size={12} strokeWidth={3} className="text-white" />
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </Group>
                        <Group label={wi.industryLabel} htmlFor="wi-industry">
                          <input
                            id="wi-industry"
                            value={industry}
                            onChange={setText("industry")}
                            placeholder={wi.industryPlaceholder}
                            enterKeyHint="next"
                            className={INPUT}
                          />
                        </Group>
                      </>
                    )}

                    {step === 1 && (
                      <>
                        <Group label={wi.featuresLabel}>
                          <div className="flex flex-wrap gap-2">
                            {wi.features.map((opt) => {
                              const on = features.includes(opt.id);
                              const Icon = FEATURE_ICONS[opt.id] ?? Sparkles;
                              return (
                                <button
                                  key={opt.id}
                                  type="button"
                                  aria-pressed={on}
                                  onClick={() => toggleFeature(opt.id)}
                                  className={`inline-flex min-h-[42px] items-center gap-2 rounded-full border px-3.5 text-[14px] transition-all duration-200 cursor-pointer ${
                                    on
                                      ? "border-[#FF419D]/50 bg-[#FF419D]/15 text-white"
                                      : "border-white/[0.08] bg-white/[0.02] text-white/60 hover:border-white/20 hover:text-white"
                                  }`}
                                >
                                  {on ? <Check size={14} className="text-[#FF6FB5]" /> : <Icon size={14} className="text-white/35" />}
                                  {opt.title}
                                </button>
                              );
                            })}
                          </div>
                        </Group>
                        <Group label={wi.pagesLabel}>
                          <Segmented options={wi.pages} value={pages} onChange={setField("pages")} />
                        </Group>
                        <Group label={wi.hasContentLabel}>
                          <Segmented options={wi.hasContent} value={hasContent} onChange={setField("hasContent")} />
                        </Group>
                        <Group label={wi.timelineLabel}>
                          <Segmented options={wi.timelines} value={timeline} onChange={setField("timeline")} />
                        </Group>
                        <Group label={wi.referenceLabel} htmlFor="wi-reference">
                          <div className="relative">
                            <Globe size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                            <input
                              id="wi-reference"
                              value={reference}
                              onChange={setText("reference")}
                              placeholder={wi.referencePlaceholder}
                              inputMode="url"
                              autoCapitalize="off"
                              enterKeyHint="next"
                              className={`${INPUT} pl-11`}
                            />
                          </div>
                        </Group>
                      </>
                    )}

                    {step === 2 && (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                          <Group label={wi.nameLabel} required htmlFor="wi-name">
                            <input id="wi-name" required autoComplete="name" value={name} onChange={setText("name")} placeholder={wi.namePlaceholder} className={INPUT} />
                          </Group>
                          <Group label={wi.phoneLabel} required htmlFor="wi-phone">
                            <input id="wi-phone" type="tel" inputMode="tel" required autoComplete="tel" value={phone} onChange={setText("phone")} placeholder={wi.phonePlaceholder} className={INPUT} />
                          </Group>
                          <Group label={wi.emailLabel} required htmlFor="wi-email">
                            <input id="wi-email" type="email" inputMode="email" required autoComplete="email" autoCapitalize="off" spellCheck={false} value={email} onChange={setText("email")} placeholder={wi.emailPlaceholder} className={INPUT} />
                          </Group>
                          <Group label={wi.companyLabel} htmlFor="wi-company">
                            <input id="wi-company" autoComplete="organization" value={company} onChange={setText("company")} placeholder={wi.companyPlaceholder} className={INPUT} />
                          </Group>
                        </div>
                        <Group label={wi.detailsLabel} htmlFor="wi-details">
                          <textarea
                            id="wi-details"
                            rows={4}
                            value={details}
                            onChange={setText("details")}
                            placeholder={wi.detailsPlaceholder}
                            className={`${INPUT} h-auto resize-none py-3.5`}
                          />
                        </Group>
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>

                <FormGuard ref={guard} />

                {formStatus === "error" && (
                  <div role="alert" className="flex items-start gap-2 rounded-xl border border-red-400/25 bg-red-400/[0.07] p-3 text-sm text-red-300">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex items-center gap-3 border-t border-white/[0.07] pt-6">
                  {step > 0 && (
                    <button
                      type="button"
                      onClick={() => goTo(step - 1)}
                      aria-label="Back"
                      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/10 text-white/70 transition-colors hover:border-white/25 hover:text-white cursor-pointer"
                    >
                      <ArrowLeft size={18} />
                    </button>
                  )}
                  {step < lastStep ? (
                    <button
                      type="submit"
                      disabled={!canAdvance}
                      aria-label="Next"
                      className="ml-auto flex h-14 w-14 sm:w-20 items-center justify-center rounded-2xl text-white shadow-[0_16px_40px_-14px_rgba(255,65,157,0.7)] transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] disabled:opacity-35 disabled:shadow-none disabled:hover:scale-100 cursor-pointer disabled:cursor-not-allowed"
                      style={{ background: "linear-gradient(135deg, #FFB76C, #FF419D)" }}
                    >
                      <ArrowRight size={20} />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={formStatus === "loading"}
                      className="flex h-14 flex-1 sm:flex-none sm:ml-auto items-center justify-center gap-2 rounded-2xl px-8 font-display text-base font-semibold text-white shadow-[0_16px_40px_-14px_rgba(255,65,157,0.7)] transition-all duration-200 hover:opacity-95 active:scale-[0.98] disabled:opacity-60 cursor-pointer disabled:cursor-not-allowed"
                      style={{ background: "linear-gradient(135deg, #FFB76C 0%, #E040A0 55%, #9B59F5 100%)" }}
                    >
                      {formStatus === "loading" ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          {wi.sending}
                        </>
                      ) : (
                        <>
                          {wi.submit} <ArrowRight size={18} />
                        </>
                      )}
                    </button>
                  )}
                </div>

                {step === lastStep && (
                  <p className="-mt-3 text-xs sm:text-[13px] text-white/40 leading-relaxed">
                    {t.formPrivacy.before}
                    <Link href="/privacy" className="text-white/70 underline underline-offset-2 transition-colors hover:text-white">
                      {t.formPrivacy.link}
                    </Link>
                    {t.formPrivacy.after}
                  </p>
                )}
              </form>
            )}
          </motion.div>

          {/* ── Live blueprint (desktop) ── */}
          <motion.div
            aria-hidden
            className="hidden lg:block lg:col-span-5 lg:sticky lg:top-28"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
          >
            <div className="overflow-hidden rounded-[24px] border border-white/[0.09] bg-[#0c0c13] shadow-[0_40px_90px_-40px_rgba(224,64,160,0.5)]">
              <div className="flex h-10 items-center gap-2 border-b border-white/[0.06] px-4">
                {["#FF5F57", "#FEBC2E", "#28C840"].map((c) => (
                  <span key={c} className="h-2.5 w-2.5 rounded-full opacity-80" style={{ background: c }} />
                ))}
                <span className="mx-auto flex h-6 w-3/5 items-center justify-center gap-1.5 truncate rounded-md bg-white/[0.05] px-3 text-[11px] text-white/45">
                  <Globe size={11} className="shrink-0" />
                  <span className="truncate">{industry || "www"}</span>
                </span>
                <span className="w-10" />
              </div>

              <div className="flex flex-col gap-4 p-5">
                {/* Hero block */}
                <div className="relative overflow-hidden rounded-xl p-5" style={{ background: "linear-gradient(135deg, rgba(255,183,108,0.18), rgba(255,65,157,0.16), rgba(155,89,245,0.14))" }}>
                  <AnimatePresence mode="wait">
                    {typeTitle ? (
                      <motion.span
                        key={typeTitle}
                        className="block font-display text-2xl text-white"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.3 }}
                      >
                        {typeTitle}
                      </motion.span>
                    ) : (
                      <motion.span key="empty" className="block h-5 w-2/3 rounded-full bg-white/15" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
                    )}
                  </AnimatePresence>
                  <span className="mt-3 block h-2 w-1/2 rounded-full bg-white/15" />
                  <span className="mt-4 inline-block h-7 w-24 rounded-full bg-white/25" />
                </div>

                {/* Feature blocks light up as they're chosen */}
                <div className="grid grid-cols-2 gap-2.5">
                  <AnimatePresence initial={false} mode="popLayout">
                    {features.length === 0
                      ? [0, 1, 2, 3].map((i) => (
                          <motion.span
                            key={`ph-${i}`}
                            layout
                            className="h-14 rounded-xl border border-dashed border-white/10"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          />
                        ))
                      : [
                          ...visibleFeatures.map((id) => {
                            const Icon = FEATURE_ICONS[id] ?? Sparkles;
                            return (
                              <motion.span
                                key={id}
                                layout
                                className="flex h-14 items-center gap-2.5 rounded-xl border border-[#FF419D]/25 bg-[#FF419D]/[0.07] px-3"
                                initial={{ opacity: 0, scale: 0.85 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.85 }}
                                transition={{ type: "spring", stiffness: 380, damping: 28 }}
                              >
                                <Icon size={16} className="shrink-0 text-[#FF6FB5]" />
                                <span className="truncate text-[12px] text-white/80">{labelFor(wi.features, id)}</span>
                              </motion.span>
                            );
                          }),
                          hiddenFeatures.length > 0 && (
                            <motion.span
                              key="more"
                              layout
                              className="flex h-14 items-center gap-2.5 rounded-xl border border-[#FF419D]/25 bg-[#FF419D]/[0.07] px-3"
                              initial={{ opacity: 0, scale: 0.85 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.85 }}
                              transition={{ type: "spring", stiffness: 380, damping: 28 }}
                            >
                              <span className="flex -space-x-1.5">
                                {hiddenFeatures.slice(0, 3).map((id) => {
                                  const Icon = FEATURE_ICONS[id] ?? Sparkles;
                                  return (
                                    <span
                                      key={id}
                                      className="flex h-6 w-6 items-center justify-center rounded-full border border-[#0c0c13] bg-[#2a1424]"
                                    >
                                      <Icon size={12} className="text-[#FF6FB5]" />
                                    </span>
                                  );
                                })}
                              </span>
                              <motion.span
                                key={hiddenFeatures.length}
                                className="font-display text-[15px] text-white/85"
                                initial={{ opacity: 0, y: 4 }}
                                animate={{ opacity: 1, y: 0 }}
                              >
                                +{hiddenFeatures.length}
                              </motion.span>
                            </motion.span>
                          ),
                        ]}
                  </AnimatePresence>
                </div>

                {/* Footer skeleton */}
                <div className="flex gap-2">
                  {[40, 25, 30].map((w) => (
                    <span key={w} className="h-2 rounded-full bg-white/[0.07]" style={{ width: `${w}%` }} />
                  ))}
                </div>
              </div>
            </div>

            {picked.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2.5">
                {picked.map((p) => (
                  <motion.span
                    key={p.label}
                    className="rounded-full border border-[#E040A0]/35 bg-[#E040A0]/[0.10] px-4 py-2 text-sm sm:text-[15px] text-white"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <span className="text-white/60">{p.label}:</span> <span className="font-medium">{p.value}</span>
                  </motion.span>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function Group({
  label,
  required,
  htmlFor,
  children,
}: {
  label: string;
  required?: boolean;
  htmlFor?: string;
  children: ReactNode;
}) {
  const Tag = htmlFor ? "label" : "span";
  return (
    <div className="flex flex-col gap-3">
      <Tag {...(htmlFor ? { htmlFor } : {})} className="text-[14px] font-medium text-white/70">
        {label}
        {required && <span className="ml-0.5 text-[#FF419D]">*</span>}
      </Tag>
      {children}
    </div>
  );
}

/** Single-choice pills that wrap on small screens; tapping the active one clears it. */
function Segmented({
  options,
  value,
  onChange,
}: {
  options: ReadonlyArray<{ id: string; title: string }>;
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const on = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? "" : opt.id)}
            className={`min-h-[42px] rounded-full border px-4 text-[14px] transition-all duration-200 cursor-pointer ${
              on
                ? "border-transparent bg-gradient-to-r from-[#FFB76C] to-[#FF419D] font-medium text-white shadow-[0_8px_24px_-10px_rgba(255,65,157,0.7)]"
                : "border-white/[0.08] bg-white/[0.02] text-white/60 hover:border-white/20 hover:text-white"
            }`}
          >
            {opt.title}
          </button>
        );
      })}
    </div>
  );
}
